import { isAxiosError } from 'axios';
import { useMemo } from 'react';
import { VariantType } from '@entur/form';
import { ErrorDetailsDto } from 'types';
import { useIntl } from 'react-intl';
import { BannerAlertBox, BannerExpandableAlertBox } from '@entur/alert';

interface ErrorMessageNewProps {
  error: Error | null;
  expandable?: boolean;
}

const ErrorMessageNew = ({ error, expandable = true }: ErrorMessageNewProps) => {
  const intl = useIntl();

  const getType = (httpCode: number): VariantType => {
    if (httpCode < 400) {
      return 'info';
    }
    if (httpCode > 399 && httpCode < 500) {
      return 'warning';
    }
    return 'error';
  };

  const derivedError = useMemo(() => {
    if (!error) {
      return null;
    }

    if (isAxiosError<ErrorDetailsDto>(error)) {
      const httpCode = error.response?.status;
      const errorMessage = error.response?.data?.message ?? error.message;
      const correlationId = error.response?.data?.correlationId ?? '';

      if (httpCode) {
        let computedTitle = intl.formatMessage({ id: 'error.defaultError' });
        let computedMessage = '';

        if (httpCode === 400) {
          computedTitle = intl.formatMessage({ id: 'warnings.warning400' });
          computedMessage = intl.formatMessage({ id: 'warnings.warningMessage400' });
        } else if (httpCode === 404) {
          computedTitle = intl.formatMessage({ id: 'warnings.warning404' });
          computedMessage = intl.formatMessage({ id: 'warnings.warningMessage404' });
        } else if (httpCode === 403) {
          computedTitle = intl.formatMessage({ id: 'warnings.warning403' });
          computedMessage = intl.formatMessage({ id: 'warnings.warningMessage403' });
        } else if (httpCode === 409) {
          computedTitle = intl.formatMessage({ id: 'warnings.warning409' });
          computedMessage = `${intl.formatMessage({ id: 'warnings.warningMessage409' })}${correlationId}`;
        } else if (httpCode === 504) {
          computedTitle = intl.formatMessage({ id: 'error.error504' });
          computedMessage = intl.formatMessage({ id: 'error.errorMessage504' });
        } else if (httpCode > 400 && httpCode < 500) {
          computedTitle = intl.formatMessage({ id: 'warnings.warning400Default' });
          computedMessage = intl.formatMessage({ id: 'warnings.warningMessage400Default' });
        } else if (httpCode > 499 && httpCode < 600) {
          computedTitle = intl.formatMessage({ id: 'error.error500Default' });
          computedMessage = `${intl.formatMessage({ id: 'error.errorMessage504Default' })}${correlationId}`;
        }

        return {
          title: computedTitle,
          message: computedMessage,
          description: errorMessage,
          type: getType(httpCode),
        };
      }

      return {
        title: intl.formatMessage({ id: 'error.defaultError' }),
        message: errorMessage,
        description: errorMessage,
        type: 'error' as VariantType,
      };
    }

    return {
      title: intl.formatMessage({ id: 'error.defaultError' }),
      message: error.message,
      description: '',
      type: 'error' as VariantType,
    };
  }, [error, intl]);

  if (!derivedError) return null;

  return (
    <>
      <b />
      {expandable ? (
        <BannerExpandableAlertBox
          variant={derivedError.type}
          title={derivedError.title}
          style={{ marginBottom: '1em' }}
        >
          {derivedError.message}
          <br />
          <br />
          {derivedError.description}
        </BannerExpandableAlertBox>
      ) : (
        <BannerAlertBox variant={derivedError.type} title={derivedError.title} style={{ marginBottom: '1em' }}>
          {derivedError.message}
          {derivedError.description ? (
            <>
              <br />
              <br />
              {derivedError.description}
            </>
          ) : null}
        </BannerAlertBox>
      )}
    </>
  );
};

export default ErrorMessageNew;
